import { useEffect, useState } from 'react';
import { UserPlus, X, Link2, Copy, Check } from 'lucide-react';
import { getAdminUid, inviteLinkFor } from '@/utils/adminIdentity';
import { BRAND } from '@/brand';

const IS_CORP = BRAND === 'corporate';
const ACCENT = IS_CORP ? '#2159B8' : '#1976D2';

// Admin action: bring on a new client by handing them this admin's invite link.
// The login is always created by the client through that link — the panel used to
// offer a manual "create the login yourself" form as well, which is gone.
export default function NewClientButton() {
  const [open, setOpen] = useState(false);
  /* The admin's own invite link. Resolved when the dialog opens rather than on
     mount, so the panel doesn't pay for it on every page that shows the button. */
  const [invite, setInvite] = useState<string | null>(null);
  const [inviteFailed, setInviteFailed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || invite || inviteFailed) return;
    let live = true;
    getAdminUid().then(uid => {
      if (!live) return;
      if (uid) setInvite(inviteLinkFor(uid)); else setInviteFailed(true);
    }).catch(() => { if (live) setInviteFailed(true); });
    return () => { live = false; };
  }, [open, invite, inviteFailed]);

  const copyInvite = async () => {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard is blocked outside a secure context — select it so Ctrl+C works.
      const el = document.getElementById('nc-invite') as HTMLInputElement | null;
      el?.select();
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 text-white font-bold text-xs px-3.5 py-2.5 rounded-xl whitespace-nowrap hover:-translate-y-0.5 transition-transform"
        style={{ background: ACCENT, boxShadow: '0 10px 22px -14px rgba(0,0,0,.5)' }}
      >
        <UserPlus className="w-4 h-4" /> New client
      </button>

      {open && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-black text-slate-900">Invite a client</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-slate-500 mb-5">Send them your invite link and they register themselves.</p>

            {/* Invite link — a normal signup URL carrying this admin's uid as the
                referral, so whoever signs up through it is attributed back here. */}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">
                <Link2 className="w-3.5 h-3.5" /> Your invite link
              </div>
              {invite ? (
                <>
                  <div className="flex items-center gap-2">
                    <input
                      id="nc-invite"
                      readOnly
                      value={invite}
                      onFocus={e => e.currentTarget.select()}
                      className="flex-1 min-w-0 rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-xs font-mono text-slate-700 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={copyInvite}
                      className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold text-white whitespace-nowrap"
                      style={{ background: copied ? '#15824E' : ACCENT }}
                    >
                      {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
                    Anyone who registers through this link is recorded as brought in by you, and shows on their client record.
                  </p>
                </>
              ) : inviteFailed ? (
                <p className="text-[11px] text-slate-500 leading-relaxed">
                  Couldn't work out which admin account you're signed in as, so there's no link to show. Signing out and back in will fix it.
                </p>
              ) : (
                <div className="h-[34px] rounded-lg bg-slate-200/70 animate-pulse" />
              )}
            </div>

            <button onClick={() => setOpen(false)} className="w-full mt-6 rounded-xl border border-slate-300 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50">Done</button>
          </div>
        </div>
      )}
    </>
  );
}
